import React, { useState, useEffect } from 'react';
import { Cookie, X, Check } from 'lucide-react';

const STORAGE_KEY = 'safetydesk-cookie-consent-v1';

/**
 * GDPR / D.Lgs. 196/2003 + Provvedimento Garante Privacy 10/06/2021 compliant cookie banner.
 *
 * SafetyDesk81 only uses STRICTLY NECESSARY cookies / localStorage (JWT auth, remember-me preference).
 * No analytics, no marketing, no third-party tracking.
 *
 * The banner therefore acts as an information notice with an explicit acceptance,
 * which is the standard pattern even for "technical only" platforms in Italy.
 */
const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, ts: new Date().toISOString() }));
    } catch (e) {
      // ignore — banner will reappear next session
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 inset-x-0 z-[200] px-3 pb-3 sm:px-6 sm:pb-6"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-start gap-4 p-5 sm:p-6">
          <div className="w-12 h-12 shrink-0 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
            <Cookie size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="cookie-banner-title" className="text-base sm:text-lg font-extrabold text-[#023F80]">
              Informativa sui cookie
            </h3>
            <p className="mt-1.5 text-xs sm:text-sm text-slate-600 leading-relaxed">
              SafetyDesk81 utilizza esclusivamente <b>cookie tecnici e archiviazione locale</b> indispensabili
              al funzionamento della piattaforma (autenticazione, sessione e preferenze d'uso).
              Non utilizziamo cookie di profilazione, analitici o di terze parti. Ai sensi dell'art. 122
              D.Lgs. 196/2003 e del Provvedimento del Garante del 10 giugno 2021, per i soli cookie tecnici
              non è richiesto consenso, ma la informiamo della loro presenza.
            </p>

            {showDetails && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] sm:text-xs text-slate-600 space-y-2" data-testid="cookie-banner-details">
                <div>
                  <b className="text-slate-800">Cookie tecnici / storage utilizzati:</b>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    <li><code>token</code> — JWT di autenticazione (localStorage, scadenza sessione)</li>
                    <li><code>user</code> — profilo utente in cache (localStorage, scadenza sessione)</li>
                    <li><code>safetydesk-cookie-consent-v1</code> — preferenza relativa a questo banner</li>
                  </ul>
                </div>
                <div>
                  <b className="text-slate-800">Titolare del trattamento:</b> Centro Sicurezza Consulenze.
                </div>
                <div>
                  <b className="text-slate-800">Durata:</b> sessione utente. Può eliminare i dati in qualsiasi
                  momento dalle impostazioni del browser.
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => persist('accepted')}
                data-testid="cookie-accept-btn"
                className="px-5 py-2 rounded-full bg-[#023F80] hover:bg-[#012C5B] text-white text-xs font-bold tracking-wider flex items-center gap-2 transition-colors"
              >
                <Check size={14} /> ACCETTO
              </button>
              <button
                onClick={() => persist('dismissed')}
                data-testid="cookie-dismiss-btn"
                className="px-5 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold tracking-wider transition-colors"
              >
                CHIUDI
              </button>
              <button
                onClick={() => setShowDetails((v) => !v)}
                data-testid="cookie-details-toggle"
                className="ml-auto text-xs font-semibold text-[#023F80] hover:underline"
              >
                {showDetails ? 'Nascondi dettagli' : 'Maggiori informazioni'}
              </button>
            </div>
          </div>

          <button
            onClick={() => persist('dismissed')}
            aria-label="Chiudi banner cookie"
            className="text-slate-400 hover:text-slate-600 transition-colors -mr-1 -mt-1"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
